import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ValidationIndices } from './shiplistTypes';
import { useFieldArray, useForm, UseFormReturn } from 'react-hook-form';
import { DetailedShipList } from '@ais-ui/api-spec/config';
import { ShiplistMutateInput } from '@ais-ui/api';
import {
  shiplistMutateFormSchema,
  ShiplistMutateFormValues,
} from './formTypes';
import { zodResolver } from '@hookform/resolvers/zod';
import { grabShiplistInitialGroups } from '../../../Groups/grabInitialGroups';
import { useGetShareGroups } from '@ais-ui/websockets';

interface MutateShiplistContextProps {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  isSubmitting: boolean;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  totalShips: number;
  currentShips: any[];
  currentPage: number;
  itemsPerPage: number;
  AddGoToPage: (page: number) => void;
  goToPage: (page: number) => void;
  totalPages: number;
  fields: any[];
  append: (data: any) => void;
  remove: (index?: number | number[]) => void;
  update: (index: number, data: any) => void;
  action: 'create' | 'edit';
  mutate: (
    input: ShiplistMutateInput,
    callbacks: {
      onSuccess?: () => void;
      onError?: (error: { response?: any }) => void;
    },
  ) => void;
  shiplist: DetailedShipList | undefined;
  isLoading: boolean;
  invalidIndices: ValidationIndices;
  setInvalidIndices: React.Dispatch<React.SetStateAction<ValidationIndices>>;
  validationErrorMessages: Record<number, string>; // Maps row index to specific error message
  setValidationErrorMessages: React.Dispatch<
    React.SetStateAction<Record<number, string>>
  >;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  filteredFields: any[];
  filteredTotalShips: number;
  filteredTotalPages: number;
  form: UseFormReturn<ShiplistMutateFormValues>;
}

const MutateShiplistContext = createContext<MutateShiplistContextProps | null>(
  null,
);

export const useMutateShiplistContext = () => {
  const context = useContext(MutateShiplistContext);
  if (!context) {
    throw new Error(
      'useMutateShiplistContext must be used within a MutateShiplistProvider',
    );
  }
  return context;
};

interface MutateShiplistProviderProps {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  shiplist: DetailedShipList | undefined;
  action: 'create' | 'edit';
  mutate: (
    input: ShiplistMutateInput,
    callbacks: {
      onSuccess?: () => void;
      onError?: (error: { response?: any }) => void;
    },
  ) => void;
  children: ReactNode;
}

export const MutateShiplistProvider: React.FC<MutateShiplistProviderProps> = ({
  showDialog,
  setShowDialog,
  shiplist,
  action,
  mutate,
  children,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: groups, isLoading } = useGetShareGroups();

  const [invalidIndices, setInvalidIndices] = useState<ValidationIndices>({
    imo: [],
    mmsi: [],
  });

  const [validationErrorMessages, setValidationErrorMessages] = useState<
    Record<number, string>
  >({});

  const form = useForm<ShiplistMutateFormValues>({
    resolver: zodResolver(shiplistMutateFormSchema),
  });
  const { fields, append, remove, update, replace } = useFieldArray({
    control: form.control,
    name: 'ships',
  });

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Only initialize once groups have loaded to ensure selectedGroups are populated correctly
    if (isInitialized || isLoading) return;

    const resetValues = {
      name: shiplist?.name || '',
      color: shiplist?.color ? `#${shiplist.color}` : '#003b5c',
      useFillShipList: false,
      selectedGroups: grabShiplistInitialGroups(shiplist, groups),
      ships:
        shiplist?.ships && shiplist.ships.length > 0
          ? shiplist.ships.map((ship) => ({
              name: ship.name || '',
              mmsi: ship.mmsi,
              imo: ship.imo,
            }))
          : [{ name: '', mmsi: undefined, imo: undefined }],
    };

    form.reset(resetValues);
    setIsInitialized(true);
    replace(resetValues.ships);
  }, [shiplist, groups, isInitialized, isLoading, form, replace]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const totalShips = useMemo(() => fields.length, [fields]);
  const totalPages = useMemo(
    () => Math.ceil(totalShips / itemsPerPage),
    [totalShips],
  );

  // Enhanced filtering logic that searches across name, MMSI, and IMO
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFields = useMemo(() => {
    if (!searchQuery.trim()) {
      return fields;
    }

    const query = searchQuery.toLowerCase().trim();

    return fields.filter((field, index) => {
      // Get the actual form values for this field
      const formValues = form.getValues(`ships.${index}`);

      // Search in name
      const name = (formValues?.name || field.name || '').toLowerCase();
      if (name.includes(query)) return true;

      // Search in MMSI
      const mmsi = (formValues?.mmsi || field.mmsi || '')
        .toString()
        .toLowerCase();
      if (mmsi.includes(query)) return true;

      // Search in IMO
      const imo = (formValues?.imo || field.imo || '').toString().toLowerCase();
      if (imo.includes(query)) return true;

      return false;
    });
  }, [fields, searchQuery, form]);

  const filteredTotalShips = useMemo(
    () => filteredFields.length,
    [filteredFields],
  );
  const filteredTotalPages = useMemo(
    () => Math.ceil(filteredTotalShips / itemsPerPage),
    [filteredTotalShips, itemsPerPage],
  );

  // Update currentShips to use filtered results with original indices - optimized for large lists
  const currentShips = useMemo(() => {
    // Create a map of field IDs to original indices for O(1) lookup
    const fieldIdToIndex = new Map(
      fields.map((field, index) => [field.id, index]),
    );

    const pagedFilteredFields = filteredFields.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage,
    );

    // Map each filtered field to include its original index using the map
    return pagedFilteredFields.map((field) => ({
      ...field,
      originalIndex: fieldIdToIndex.get(field.id) ?? 0,
    }));
  }, [filteredFields, currentPage, itemsPerPage, fields]);

  // Update pagination functions to work with filtered results
  const goToPage = useCallback(
    (page: number) => {
      setCurrentPage((_prevPage) =>
        Math.max(1, Math.min(page, filteredTotalPages)),
      );
    },
    [filteredTotalPages],
  );

  const AddGoToPage = useCallback(
    (page: number) => {
      setCurrentPage((_prevPage) =>
        Math.max(1, Math.max(page, filteredTotalPages)),
      );
    },
    [filteredTotalPages],
  );

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const value = {
    showDialog,
    setShowDialog,
    isSubmitting,
    setIsSubmitting,
    totalShips,
    currentShips,
    currentPage,
    itemsPerPage,
    AddGoToPage,
    goToPage,
    totalPages: filteredTotalPages, // Use filtered total pages for pagination
    fields,
    append,
    remove,
    update,
    action,
    mutate,
    shiplist,
    isLoading,
    invalidIndices,
    setInvalidIndices,
    validationErrorMessages,
    setValidationErrorMessages,
    form,
    searchQuery,
    setSearchQuery,
    filteredFields,
    filteredTotalShips,
    filteredTotalPages,
  };

  if (!isInitialized) {
    return null; // Or return a loading state if you prefer
  }

  return (
    <MutateShiplistContext.Provider value={value}>
      {children}
    </MutateShiplistContext.Provider>
  );
};
